import { useMutation } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import type { Device, Connection, SignalType } from '@/types/devices'
import type { Zone } from '@busswerks/shared'

interface AILayoutSuggestion {
  sourceDeviceId: string
  sourcePortId: string
  targetDeviceId: string
  targetPortId: string
  signalType: string
  reason: string
  confidence: number
}

interface AIResponse {
  suggestions: AILayoutSuggestion[]
  explanation?: string
  modelUsed?: string
}

interface AskAiVariables {
  devices: Device[]
  connections: Connection[]
  zones: Zone[]
}

// Polling config. The backend Lambda has a 60s timeout, so there's
// no point polling longer than that plus a small buffer for the
// async invoke to land and the row to become visible.
const POLL_INTERVAL_MS = 1000
const POLL_TIMEOUT_MS = 75_000

interface JobPostResponse {
  jobId: string
  status: 'pending'
}

interface JobStatusResponse {
  jobId: string
  status: 'pending' | 'done' | 'failed'
  result?: AIResponse
  error?: string
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Hook for the "Smart Route" button (AI path). The request is async on the backend:
 *   1. POST /api/v1/layouts/:id/ai/suggest → returns {jobId} immediately
 *      (202) after self-invoking the worker Lambda.
 *   2. Poll GET /api/v1/ai/jobs/:jobId every second until the row
 *      reports status='done' or 'failed'.
 * Returns the same AIResponse shape the old synchronous endpoint
 * did, so EditorClient doesn't need to branch.
 */
export function useAiSuggest(layoutId: string | null) {
  return useMutation({
    mutationFn: async ({
      devices,
      connections,
      zones,
    }: AskAiVariables): Promise<AIResponse> => {
      if (!layoutId || layoutId === 'local-dev') {
        throw new Error('Smart Route requires a cloud layout')
      }
      const post = await apiClient.post<JobPostResponse>(
        `/api/v1/layouts/${layoutId}/ai/suggest`,
        { devices, connections, zones },
      )
      if (!post?.jobId) {
        // Swap a generic error for one that surfaces what the server
        // actually returned — the most common causes of a missing
        // `jobId` are (a) the server returned HTML because the route
        // isn't deployed (CloudFront fallback), (b) the server
        // returned a plain-text or 204-empty response, or (c) the
        // response JSON had a different shape (e.g. legacy pair-mode
        // synchronous response when we expected async job mode).
        // Widen to unknown so the runtime discriminant works — the
        // typed response says `JobPostResponse`, but in practice
        // apiClient may return text when the backend returned HTML.
        const postRaw: unknown = post
        const preview =
          typeof postRaw === 'string'
            ? postRaw.slice(0, 120).replace(/\s+/g, ' ')
            : JSON.stringify(postRaw).slice(0, 120)
        throw new Error(
          `Smart Route job did not start. Server responded with: ${preview || '<empty>'} ` +
            '— most likely the ai-service Lambda needs a `npm run build:api && npm run deploy:dev` to pick up the latest routes.',
        )
      }

      const start = Date.now()
      while (Date.now() - start < POLL_TIMEOUT_MS) {
        await sleep(POLL_INTERVAL_MS)
        const status = await apiClient.get<JobStatusResponse>(
          `/api/v1/ai/jobs/${post.jobId}`,
        )
        if (status.status === 'done') {
          return (
            status.result ?? {
              suggestions: [],
              explanation: 'AI job returned no result',
              modelUsed: 'unknown',
            }
          )
        }
        if (status.status === 'failed') {
          throw new Error(status.error ?? 'AI job failed')
        }
      }
      throw new Error(
        `Smart Route timed out after ${POLL_TIMEOUT_MS / 1000}s. The layout may be too large — try drilling into a zone first.`,
      )
    },
  })
}

export type { AILayoutSuggestion, AIResponse, SignalType }
